import { ArrowDownRight, Bookmark, Euro, Percent, Settings2, Sparkles, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COLUMNS, DEFAULT_COLUMNS } from "@/components/opportunities/columns";
import { eur, num, pct } from "@/lib/format";

const Item = ({ icon: Icon, iconColor, value, label, tone = "text-slate-900", testid }) => (
  <div className="flex items-center gap-2 pr-5" data-testid={testid}>
    {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconColor || "text-slate-400"}`} />}
    <div>
      <div className={`mono text-[17px] font-semibold leading-none ${tone}`}>{value}</div>
      <div className="label-xs mt-1 whitespace-nowrap">{label}</div>
    </div>
  </div>
);

export const KpiBar = ({ kpis, columns, onColumns }) => (
  <div
    className="panel mb-3 flex flex-wrap items-center gap-y-3 divide-slate-200 px-3 py-2.5"
    data-testid="kpi-bar"
  >
    <Item
      icon={Bookmark}
      value={num(kpis.count)}
      label="Totaal voertuigen"
      testid="kpi-total"
    />
    <Item
      icon={Sparkles}
      iconColor="text-emerald-600"
      value={num(kpis.new_count)}
      label="Nieuw"
      tone={kpis.new_count ? "text-emerald-700" : "text-slate-400"}
      testid="kpi-new"
    />
    <Item
      icon={ArrowDownRight}
      iconColor="text-amber-600"
      value={num(kpis.price_drop_count)}
      label="Prijs verlaagd"
      tone={kpis.price_drop_count ? "text-amber-700" : "text-slate-400"}
      testid="kpi-price-drop"
    />
    <Item
      icon={XCircle}
      iconColor="text-red-500"
      value={num(kpis.disappeared_count)}
      label="Verdwenen"
      tone={kpis.disappeared_count ? "text-red-700" : "text-slate-400"}
      testid="kpi-disappeared"
    />
    <Item
      icon={Euro}
      value={eur(kpis.total_contribution)}
      label="Totale potentiële bijdrage"
      tone="text-emerald-700"
      testid="kpi-contribution"
    />
    <Item
      icon={Percent}
      value={pct(kpis.avg_roi, { fallback: "—" })}
      label="Gem. ROI"
      testid="kpi-roi"
    />
    <Item
      icon={Euro}
      value={eur(kpis.avg_contribution, { fallback: "—" })}
      label="Gem. winst per auto"
      testid="kpi-avg-contribution"
    />

    <div className="ml-auto">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="column-picker-trigger"
            className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:border-blue-400 hover:text-blue-700"
          >
            <Settings2 className="h-3.5 w-3.5" /> Kolommen aanpassen
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2" data-testid="column-picker">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="label-xs">Zichtbare kolommen</span>
            <button
              type="button"
              onClick={() => onColumns(DEFAULT_COLUMNS)}
              data-testid="column-picker-reset"
              className="text-[11px] font-semibold text-blue-700 hover:underline"
            >
              Standaard
            </button>
          </div>
          <div className="max-h-72 space-y-0.5 overflow-y-auto">
            {COLUMNS.map((c) => {
              const on = columns.includes(c.key);
              return (
                <label
                  key={c.key}
                  className={`flex items-center gap-2 rounded-sm px-1.5 py-1 text-[12px] ${
                    c.locked ? "text-slate-400" : "cursor-pointer text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={c.locked}
                    data-testid={`column-toggle-${c.key}`}
                    onChange={() =>
                      onColumns(
                        on
                          ? columns.filter((key) => key !== c.key)
                          : COLUMNS.filter((x) => x.key === c.key || columns.includes(x.key)).map(
                              (x) => x.key
                            )
                      )
                    }
                    className="h-3.5 w-3.5 accent-blue-600"
                  />
                  {c.label}
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
);
